import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, X, PartyPopper } from "lucide-react";

// Detects visitors returning from Play Store (referrer or ?from=play/utm_source=play)
function isPlayReferrer() {
  if (typeof document === "undefined") return false;
  try {
    const ref = (document.referrer || "").toLowerCase();
    if (ref.includes("play.google.com") || ref.includes("android-app://com.android.vending")) {
      return true;
    }
    const p = new URLSearchParams(window.location.search);
    const from = (p.get("from") || "").toLowerCase();
    const src = (p.get("utm_source") || "").toLowerCase();
    if (from === "play" || from === "playstore" || src === "play" || src === "googleplay") {
      return true;
    }
  } catch (_) { /* ignore */ }
  return false;
}

export default function BetaThanksToast() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Only show once per session
    try {
      if (sessionStorage.getItem("ix.beta.thanked") === "1") return;
    } catch (_) { /* ignore */ }
    if (!isPlayReferrer()) return;
    try { sessionStorage.setItem("ix.beta.thanked", "1"); } catch (_) { /* ignore */ }
    const t = setTimeout(() => setShow(true), 600);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => setShow(false), 8000);
    return () => clearTimeout(t);
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.95 }}
          transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
          className="fixed bottom-5 left-5 md:bottom-8 md:left-8 z-40 max-w-sm"
          data-testid="beta-thanks-toast"
          role="status"
          aria-live="polite"
        >
          <div className="relative rounded-2xl bg-forest text-canvas p-4 pr-10 shadow-[0_20px_60px_-15px_rgba(45,13,94,0.6)] ring-1 ring-lime/40 overflow-hidden">
            <div className="pointer-events-none absolute -right-10 -top-10 w-32 h-32 rounded-full bg-lime/15 blur-2xl" />
            <div className="relative flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-lime text-forest grid place-items-center shrink-0">
                <PartyPopper className="w-4 h-4" strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-lime">
                  Beta Tester
                </div>
                <div className="mt-0.5 font-display text-lg tracking-tight text-canvas leading-tight">
                  Thanks for testing! 🌱
                </div>
                <div className="mt-1 text-xs text-canvas/70 leading-snug">
                  You&apos;re shaping what launches for millions. Ping{" "}
                  <a href="mailto:founder@instantxfer.com" className="underline text-lime">
                    founder@instantxfer.com
                  </a>{" "}
                  with any bugs — we read every one.
                </div>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-canvas/50">
                  <CheckCircle2 className="w-3 h-3 text-lime" strokeWidth={2.4} /> Detected Play Store return
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShow(false)}
              aria-label="Dismiss"
              data-testid="beta-thanks-dismiss"
              className="absolute right-2 top-2 w-7 h-7 rounded-full hover:bg-canvas/10 transition-colors grid place-items-center"
            >
              <X className="w-3.5 h-3.5" strokeWidth={2.4} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
